import {Ref, onUnmounted, ref, watch} from "vue";
import {usePreferredReducedMotion} from "@vueuse/core";

/** Delay between two typed characters. */
const TYPE_MS = 45;

/** Erasing runs faster than typing, as a real correction would. */
const ERASE_MS = 20;

/** How long a finished sentence stays readable before it is erased. */
const HOLD_MS = 1800;

/** Beat on the empty field, before the next sentence starts. */
const BLANK_MS = 400;

/**
 * Type a list of sentences one after another, as if someone were writing them.
 *
 * Used for the composer's placeholder: an assistant that shows what can be
 * asked invites a question far better than a static label, and far better than
 * buttons — which read as the only options available.
 *
 * @param sentences
 * @param enabled Typing stops as soon as the field is the pharmacist's.
 */
const useTypewriter = (sentences: string[], enabled: Ref<boolean>) => {
    const text = ref<string>('');
    const reducedMotion = usePreferredReducedMotion();

    let timer: ReturnType<typeof setTimeout> | null = null;
    let sentence = 0;
    let length = 0;
    let erasing = false;

    const stop = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const schedule = (delay: number) => {
        timer = setTimeout(tick, delay);
    };

    function tick(): void {
        const current = sentences[sentence] ?? '';

        if (erasing) {
            length = Math.max(0, length - 1);
            text.value = current.slice(0, length);

            if (length === 0) {
                erasing = false;
                sentence = (sentence + 1) % sentences.length;
                schedule(BLANK_MS);

                return;
            }

            schedule(ERASE_MS);

            return;
        }

        length = Math.min(current.length, length + 1);
        text.value = current.slice(0, length);

        if (length === current.length) {
            erasing = true;
            schedule(HOLD_MS);

            return;
        }

        schedule(TYPE_MS);
    }

    /**
     * Someone who asked for less motion gets the first sentence, whole and
     * still.
     */
    const start = () => {
        if (timer || !sentences.length) {
            return;
        }

        if (reducedMotion.value === 'reduce') {
            text.value = sentences[0];

            return;
        }

        schedule(TYPE_MS);
    };

    // Resumes where it stopped rather than restarting: a sentence half written
    // when the pharmacist clicked away carries on when they click elsewhere.
    watch(enabled, isEnabled => isEnabled ? start() : stop(), {immediate: true});

    onUnmounted(stop);

    return {text};
};

export default useTypewriter;
